// Which dependencies an agent touched, and why it was looking.
//
//   cargo add tokio --features full
//   cargo tree -i zstd-sys -e features
//   python -m pip install libclang --target /c/tmp/libclang_pkg
//
// The target is the PACKAGE NAME, which is about as identifiable as a target gets:
// it is a coordinate in a public registry, identical for every tenant, and it
// outlives every session by definition.
//
// Two acts that look alike and are not: CHANGING the tree (add, remove, bump) and
// INTERROGATING it (`cargo tree -i x` asks "why is this here at all", which is a
// question about a dependency someone else introduced). An inverse tree query is
// usually a symptom — somebody is chasing a build failure or a licence — and it is
// the one that says the most about what the session was struggling with.

import { flagOf, positionalsOf, shellStatements } from "../../src/mine/statements.js";

const VALUE_FLAGS = new Set(["--features", "-F", "--manifest-path", "-p", "--package", "--target",
  "--version", "-e", "--edges", "-i", "--invert", "--depth", "--index", "--registry", "--save-dev", "--cert"]);

const ECOSYSTEM = {
  cargo: "crates.io", npm: "npm", pnpm: "npm", yarn: "npm", bun: "npm",
  pip: "pypi", pip3: "pypi", uv: "pypi", poetry: "pypi", go: "go", gem: "rubygems",
};

const ADD = /^(?:add|install|i|require)$/i;
const REMOVE = /^(?:remove|rm|uninstall|un)$/i;
const BUMP = /^(?:update|upgrade|up)$/i;
const INSPECT = /^(?:tree|why|ls|list|show|info|outdated|audit|deps)$/i;

export function ecosystemOf(program, text) {
  if (program === "python" || program === "python3") return /\bpip\b/.test(text) ? "pypi" : null;
  return ECOSYSTEM[program] || null;
}

export function kindOf(verb) {
  if (ADD.test(verb)) return "add";
  if (REMOVE.test(verb)) return "remove";
  if (BUMP.test(verb)) return "bump";
  if (INSPECT.test(verb)) return "inspect";
  return null;
}

/**
 * The packages named. A version pinned inline (`tokio@1.2`, `libclang==1.0`) is the
 * intent; the version a tree REPORTS is the observation, and they are different
 * facts kept apart.
 */
export function packagesOf(bare, verb) {
  const at = bare.indexOf(verb);
  const rest = bare.slice(at + 1).filter((t) => !/^[-.\/]/.test(t) && !/^(?:python|-m|pip|install)$/i.test(t));
  return rest.map((raw) => {
    const m = /^([@\w./-]+?)(?:[@=]{1,2}([\w.*^~>=<-]+))?$/.exec(raw);
    return m ? { name: m[1], wanted: m[2] || null } : null;
  }).filter((p) => p && /^[@a-z]/i.test(p.name)).slice(0, 8);
}

/** The version a tree or list actually reported for a package. */
export function reportedVersion(pkg, output) {
  if (!output || !pkg) return null;
  const esc = pkg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = new RegExp(`\\b${esc}[@ ]v?(\\d+\\.\\d+(?:\\.\\d+)?[\\w.+-]*)`).exec(output);
  return m ? m[1] : null;
}

const FAILED_RE = /\berror\b[:\s]|\bE\d{3}\b|could not (?:find|compile)|no matching (?:package|version)|ERROR: |connection broken|SSLError/i;

export function extractDepsOps(turns) {
  const ops = [];
  for (const st of shellStatements(turns)) {
    const eco = ecosystemOf(st.program, st.text);
    if (!eco) continue;
    const bare = positionalsOf(st.args, VALUE_FLAGS);
    const verb = bare.find((t) => kindOf(t));
    if (!verb) continue;
    const kind = kindOf(verb);

    // `cargo tree -i x` inverts the tree: the question is "what pulls x in", which
    // is a different question from "what does this crate depend on".
    const inverted = kind === "inspect" && /(?:^|\s)(?:-i|--invert)\b/.test(st.text);
    const explicit = flagOf(st.args, "-i", "--invert", "-p", "--package");
    const packages = explicit && !explicit.startsWith("-")
      ? [{ name: explicit, wanted: null }]
      : packagesOf(bare, verb);
    if (!packages.length) continue;

    const out = st.output;
    ops.push({
      key: st.key,
      ecosystem: eco,
      kind,
      inverted,
      question: inverted ? "what pulls this in" : null,
      packages: packages.map((p) => p.name),
      wanted: packages.find((p) => p.wanted)?.wanted || null,
      // The version the tool REPORTED — an observation, not an intent.
      reported: out ? reportedVersion(packages[0]?.name, out) : null,
      manifest: flagOf(st.args, "--manifest-path"),
      outcome: !out ? "unrecorded" : FAILED_RE.test(out) ? "failed" : "answered",
      output: out,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind: inverted ? "why is this here" : kind, cmd: st.text.replace(/\s+/g, " ").slice(0, 240) }],
      checks: [{
        kind, tool: packages.map((p) => p.name).join(", ").slice(0, 60),
        scope: [
          out && reportedVersion(packages[0]?.name, out) ? `v${reportedVersion(packages[0]?.name, out)}` : null,
          inverted ? "asked what pulls it in" : null,
          eco,
        ].filter(Boolean),
        outcome: !out ? "unrecorded" : FAILED_RE.test(out) ? "failed" : "answered",
      }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — what the package manager reported never entered this trace";
    case "shell-error": return "the captured text was the shell's error, not the package manager's";
    case "none": return "ran and captured no output";
    default: break;
  }
  if (op.outcome === "failed") return "the package manager failed — the tree was not changed and the question went unanswered";
  return null;
}
